import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Star, Trash2, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";

const EMPTY = { name: "", address_line1: "", address_line2: "", city: "", state: "", zip: "", phone: "", is_default: false };

/**
 * Prescriber-only "My Clinics" management.
 *
 * Each prescriber maintains their own roster of in-person clinic addresses
 * they practice at. These addresses are then selectable in the eRx modal's
 * "Ship to Clinic" toggle so the medication is dispensed to the office
 * instead of the patient's home (in-person administration use cases).
 */
export default function MyClinicsTab() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null, or { id?, ...form }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/prescriber/clinics");
      setClinics(data);
    } catch { toast.error("Failed to load clinics"); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    if (!editing.name?.trim() || !editing.address_line1?.trim() || !editing.city?.trim() || !editing.state?.trim() || !editing.zip?.trim()) {
      toast.error("Name and full address are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.id;
      if (editing.id) {
        await API.put(`/prescriber/clinics/${editing.id}`, payload);
        toast.success("Clinic updated");
      } else {
        await API.post("/prescriber/clinics", payload);
        toast.success("Clinic added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!(await brandedConfirm({ title: 'Delete?', description: "Delete this clinic? It will no longer be available in the eRx Ship-to-Clinic dropdown.", confirmText: 'Delete', cancelText: 'Cancel', tone: 'danger' }))) return;
    try {
      await API.delete(`/prescriber/clinics/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="my-clinics-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>My Clinics</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Clinic addresses you can ship eRx medications to (instead of the patient&apos;s home).
          </p>
        </div>
        <Button
          className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="add-clinic-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Add Clinic
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400 dark:text-slate-500">Loading...</div>
      ) : clinics.length === 0 ? (
        <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl border border-slate-300 dark:border-slate-700 p-12 text-center" data-testid="no-clinics-empty">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>No clinics yet</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">Add a clinic so you can flip &quot;Ship to Clinic&quot; on the eRx form for in-person administration.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {clinics.map(c => (
            <div key={c.id} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4" data-testid={`clinic-${c.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-purple-700 dark:text-purple-300" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white text-sm">{c.name}</p>
                    {c.is_default && (
                      <p className="text-[10px] uppercase tracking-wider text-purple-700 dark:text-purple-300 font-bold flex items-center gap-0.5">
                        <Star className="w-3 h-3 fill-current" /> Default
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(c)} data-testid={`edit-clinic-${c.id}`}>
                    <Edit3 className="w-4 h-4 text-slate-500" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(c.id)} data-testid={`delete-clinic-${c.id}`}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                <p>{c.address_line1}</p>
                {c.address_line2 && <p>{c.address_line2}</p>}
                <p>{c.city}, {c.state} {c.zip}</p>
                {c.phone && <p className="text-slate-400 dark:text-slate-500 mt-1">{c.phone}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit modal */}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="clinic-edit-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
              {editing?.id ? "Edit Clinic" : "Add Clinic"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">Clinic Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Sunset Family Medicine" className="mt-1 rounded-xl" data-testid="clinic-name-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Address Line 1</Label>
                <Input value={editing.address_line1} onChange={(e) => setEditing({ ...editing, address_line1: e.target.value })} placeholder="123 Main St" className="mt-1 rounded-xl" data-testid="clinic-addr1-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Address Line 2 (optional)</Label>
                <Input value={editing.address_line2} onChange={(e) => setEditing({ ...editing, address_line2: e.target.value })} placeholder="Suite 200" className="mt-1 rounded-xl" data-testid="clinic-addr2-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-sm font-medium">City</Label>
                  <Input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} className="mt-1 rounded-xl" data-testid="clinic-city-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">State</Label>
                  <Input value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value.toUpperCase() })} maxLength={2} placeholder="CA" className="mt-1 rounded-xl uppercase" data-testid="clinic-state-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">ZIP</Label>
                  <Input value={editing.zip} onChange={(e) => setEditing({ ...editing, zip: e.target.value })} className="mt-1 rounded-xl" data-testid="clinic-zip-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Clinic Phone</Label>
                  <Input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="mt-1 rounded-xl" data-testid="clinic-phone-input" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm pt-1">
                <input
                  type="checkbox"
                  checked={!!editing.is_default}
                  onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })}
                  data-testid="clinic-default-checkbox"
                />
                Make this my default clinic
              </label>
              {editing.is_default && (
                <p className="text-[11px] text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 rounded-md px-2 py-1.5 -mt-1" data-testid="clinic-default-sync-hint">
                  Saving will auto-fill your Practice Address (used on eRx PDFs) with this clinic&apos;s address.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-clinic-btn">
              {saving ? "Saving..." : (editing?.id ? "Save Changes" : "Add Clinic")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
